import './styles/app.css';
import { createEditor } from '@/editor/editor';
import { HeaderFooter } from '@/editor/headerFooter';
import { buildRibbon } from '@/ribbon/ribbon';
import { FormatPainter } from '@/editor/formatPainter';
import { FindPanel } from '@/ui/findPanel';
import { FileExplorer } from '@/ui/fileExplorer';
import { StatusBar } from '@/ui/statusBar';
import { registerShortcuts } from '@/editor/shortcuts';
import { store, type AppState } from '@/state/store';
import { PAGE_DIMENSIONS, type PageSetup } from '@/state/types';
import { verifySession, loginRedirectUrl } from '@/auth/auth';
import * as docSvc from '@/files/docService';
import { openPrintPreview } from '@/files/printPreview';
import { isGoogleConfigured } from '@/files/googleDrive';
import { CommentsPanel } from '@/ui/commentsPanel';
import { ReviewPanel } from '@/ui/reviewPanel';
import { isTrackingEnabled, setTracking, applyMarkupView, getMarkupView } from '@/editor/trackChangesService';
import { icon } from '@/icons/icons';

// ═══════════════════════════════════════════════════════════════
// Bootstrap DocEditoru (Fáze 1 MVP)
// ═══════════════════════════════════════════════════════════════

async function boot() {
  const app = document.getElementById('app')!;

  // 1) Ověř sdílenou session (auth-worker.js). Gate před editorem.
  await verifySession();
  const state = store.get();
  if (!state.user) {
    renderAuthGate();
    return;
  }

  // 2) Postav layout.
  app.innerHTML = `
    <header class="titlebar">
      <span class="brand">Doc<span class="dot">·</span>Editor <span style="font-size:10px;opacity:.5;font-weight:400">v2.0</span></span>
      <span class="doc-name" id="doc-name"></span>
      <span class="spacer"></span>
      <div class="quick" id="quick"></div>
      <div class="user" id="user"></div>
    </header>
    <div id="ribbon-root"></div>
    <div class="workspace">
      <div id="explorer-root"></div>
      <div class="canvas" id="canvas">
        <div class="page-wrap" id="page-wrap">
          <div class="doc-page" id="doc-page"></div>
        </div>
      </div>
      <div id="comments-root"></div>
      <div id="review-root"></div>
    </div>
    <div id="status-root"></div>`;

  // 3) Editor v A4 stránce.
  const page = document.getElementById('doc-page')!;
  const editor = createEditor(page);
  docSvc.bindEditor(editor);

  // 3b) Záhlaví a zápatí — panely nad a pod stránkou (Fáze 2).
  const headerFooter = new HeaderFooter(page);
  headerFooter.mountAround(page);
  docSvc.bindHeaderFooter(headerFooter);

  // 4) Format painter — aplikace při dokončení výběru (mouseup i klávesnice).
  const painter = new FormatPainter(editor, (active) => {
    // Vizuální stav tlačítka + kurzor plátna.
    const btn = document.querySelector('[data-toggle="painter"]');
    if (btn) btn.classList.toggle('active', active);
    page.style.cursor = active ? 'copy' : '';
  });
  page.addEventListener('mouseup', () => painter.onSelectionApply());
  // Escape zruší painter.
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && painter.active) painter.cancel();
  });

  // 5) Ribbon + find panel + shortcuts.
  const findPanel = new FindPanel(document.getElementById('canvas')!, editor);
  // Panel komentářů (Fáze 3) — vpravo od dokumentu.
  const commentsPanel = new CommentsPanel(document.getElementById('comments-root')!, editor);

  // Panel revizí (Fáze 3) — sledované změny.
  const reviewPanel = new ReviewPanel(document.getElementById('review-root')!, editor);

  // Režim zobrazení revizí — promítni uložený stav do DOM (třída na stránce).
  applyMarkupView(getMarkupView());

  buildRibbon(
    document.getElementById('ribbon-root')!,
    editor,
    painter,
    () => findPanel.show('find'),
    () => commentsPanel.addForSelection(),
    () => commentsPanel.toggle(),
    () => {
      // Přepnutí sledování změn + zvýraznění tlačítka.
      const on = !isTrackingEnabled();
      setTracking(on);
      document
        .querySelectorAll('[data-role="tracking-toggle"]')
        .forEach((b) => b.classList.toggle('is-on', on));
      // Při zapnutí otevřeme panel revizí — ať je vidět, co se děje.
      if (on) reviewPanel.show();
    },
    () => reviewPanel.toggle(),
  );

  reviewPanel.onVisibilityChange = (open) => {
    document
      .querySelectorAll('[data-role="review-toggle"]')
      .forEach((b) => b.classList.toggle('is-on', open));
  };

  // Zvýraznění přepínače panelu komentářů podle jeho stavu.
  commentsPanel.onVisibilityChange = (open) => {
    document
      .querySelectorAll('[data-role="comments-toggle"]')
      .forEach((b) => b.classList.toggle('is-on', open));
  };
  registerShortcuts(findPanel);

  // 6) Quick akce v titlebaru (Nový, Otevřít, Uložit).
  buildQuickActions(document.getElementById('quick')!, editor, commentsPanel);

  // 7) Průzkumník souborů + stavový řádek.
  new FileExplorer(document.getElementById('explorer-root')!);
  new StatusBar(document.getElementById('status-root')!, (z) => applyZoom(z));

  // 8) Uživatel v titlebaru.
  renderUser(state);

  // 9) Reaktivní překreslení stránky (PageSetup, dirty název).
  store.subscribe((s) => {
    applyPageSetup(page, s.doc.pageSetup);
    renderDocName(s);
  });
  applyPageSetup(page, state.doc.pageSetup);

  // 10) Autosave a varování při zavření s neuloženými změnami.
  docSvc.startAutosave();
  window.addEventListener('beforeunload', (e) => {
    if (store.get().doc.dirty) { e.preventDefault(); e.returnValue = ''; }
  });
}

// ── Rozměry stránky dle PageSetup ────────────────────────────
function applyPageSetup(page: HTMLElement, ps: PageSetup) {
  const dims = PAGE_DIMENSIONS[ps.size];
  const landscape = ps.orientation === 'landscape';
  const wmm = landscape ? dims.h : dims.w;
  const hmm = landscape ? dims.w : dims.h;

  page.style.width = wmm + 'mm';

  // Živé stránkování: .doc-page je plátno, .doc-page-content je bílý list
  // pevné výšky A4. Předáme okraje + využitelnou výšku těla jako CSS proměnné.
  page.classList.add('pm-paginated');
  page.style.setProperty('--pg-mt', ps.margins.top + 'mm');
  page.style.setProperty('--pg-mr', ps.margins.right + 'mm');
  page.style.setProperty('--pg-mb', ps.margins.bottom + 'mm');
  page.style.setProperty('--pg-ml', ps.margins.left + 'mm');
  page.style.setProperty('--pg-gap', '16px');
  // Plná výška listu (A4) — fallback min-height pro plnou první/jedinou stránku.
  page.style.setProperty('--pg-page-h', hmm + 'mm');

  // Rezerva na záhlaví/zápatí (opakují se — místo se musí odečíst z těla).
  const s = store.get();
  const hasHeader = !!s.doc.headerHtml?.trim();
  const hasFooter = !!s.doc.footerHtml?.trim() || !!(s.doc.pageNumber && s.doc.pageNumber.enabled);
  const hfTop = hasHeader ? 8 : 0;
  const hfBottom = hasFooter ? 8 : 0;

  // Využitelná výška těla listu = A4 výška − horní/dolní okraj − HF rezerva.
  const bodyMm = hmm - ps.margins.top - ps.margins.bottom - hfTop - hfBottom;
  page.style.setProperty('--pg-body-h', bodyMm + 'mm');

  // Bez pevného minHeight na plátně — výšku listu řeší --pg-body-h + padding.
  page.style.minHeight = '';
}

function applyZoom(z: number) {
  store.set({ zoom: z });
  const wrap = document.getElementById('page-wrap');
  if (wrap) wrap.style.transform = `scale(${z})`;
}

// ── Quick akce ───────────────────────────────────────────────
function buildQuickActions(root: HTMLElement, editor: import('@tiptap/core').Editor, commentsPanel: CommentsPanel) {
  const mk = (ic: Parameters<typeof icon>[0], title: string, fn: () => void) => {
    const b = document.createElement('button');
    b.title = title;
    b.innerHTML = icon(ic, 16);
    b.onclick = fn;
    return b;
  };
  root.append(
    mk('newDoc', 'Nový dokument', () => void docSvc.newDocument()),
    mk('open', 'Otevřít…', () => void docSvc.openViaPicker()),
    mk('save', 'Uložit (Ctrl+S)', () => void docSvc.save()),
    mk('pdf', 'Stránkovaný náhled (tisk / PDF)', () => openPrintPreview(editor)),
    mk('comment', 'Komentáře', () => commentsPanel.toggle()),
  );

  // Google Disk — jen když je nakonfigurovaný (VITE_GOOGLE_CLIENT_ID).
  if (isGoogleConfigured()) {
    root.append(
      mk('gdrive', 'Otevřít z Google Disku', () => void docSvc.openFromDrive()),
      mk('gdriveSave', 'Uložit na Google Disk', () => void docSvc.saveToDriveAs()),
    );
  }
}

function renderDocName(s: AppState) {
  const el = document.getElementById('doc-name');
  if (!el) return;
  el.innerHTML = `${s.doc.name}${s.doc.dirty ? '<span class="dirty">•</span>' : ''}`;
}

function renderUser(s: AppState) {
  const el = document.getElementById('user');
  if (!el || !s.user) return;
  const pic = s.user.picture ? `<img src="${s.user.picture}" alt="" />` : '';
  el.innerHTML = `${pic}<span>${s.user.name || s.user.email}</span>`;
}

// ── Auth gate ────────────────────────────────────────────────
function renderAuthGate() {
  const app = document.getElementById('app')!;
  app.innerHTML = `
    <div class="auth-gate">
      <div class="auth-card">
        <h1>Přihlaste se</h1>
        <p>DocEditor používá váš účet Janette.ai. Přihlaste se a otevřete DocEditor znovu z rozcestníku.</p>
        <a class="btn" href="${loginRedirectUrl()}">Přejít na přihlášení</a>
      </div>
    </div>`;
}

void boot();
